import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GetAllEventsByOrganiserId, GetFunctionsByEventId, GetGuestsByEventId } from '../../api/api';
import { isMarriageEventType } from '../../utils/helpers';

const guestKey = (row) => {
  const owner = row.CreatedBy || 'admin';
  const mobile = String(row.MobileNumber || '').trim();
  if (mobile) return `${owner}:mobile:${mobile}`;
  return `${owner}:name:${[row.Surname, row.GuestName, row.FatherHusband].map(v => String(v || '').trim().toLowerCase()).join('|')}`;
};

const uniqueGuestParties = (rows) => {
  const groups = new Map();
  rows.forEach(row => {
    const key = guestKey(row);
    if (!groups.has(key)) groups.set(key, { ...row, ExpectedCount: Number(row.ExpectedCount || 1) });
    else {
      const existing = groups.get(key);
      existing.ExpectedCount = Math.max(Number(existing.ExpectedCount || 1), Number(row.ExpectedCount || 1));
    }
  });
  return Array.from(groups.values());
};

export function useDashboard() {
  // The user's own side (chosen once on My Profile) decides which per-side
  // figure is highlighted — no toggle asks for it any more.
  const { organiser, event, mySide } = useAuth();
  const [kpi, setKpi] = useState({ events: 0, functions: 0, guests: 0, brideGuests: 0, groomGuests: 0, pax: 0 });
  const isMarriage = isMarriageEventType(event?.EventTypeName || event?.EventType);
  const guestSide = mySide || 'Groom';

  useEffect(() => {
    if (!organiser) return;
    GetAllEventsByOrganiserId({ OrganiserId: organiser.OrganiserId })
      .then(e => setKpi(k => ({ ...k, events: Array.isArray(e) ? e.length : 0 }))).catch(() => {});
  }, [organiser]);

  useEffect(() => {
    if (!event) { setKpi(k => ({ ...k, functions: 0, guests: 0, brideGuests: 0, groomGuests: 0, pax: 0 })); return; }
    Promise.all([
      GetFunctionsByEventId({ EventId: event.EventId }),
      GetGuestsByEventId({ EventId: event.EventId }),
    ]).then(([f, g]) => {
      const functions = Array.isArray(f) ? f : [];
      const guests = uniqueGuestParties(Array.isArray(g) ? g : []);
      setKpi(k => ({
        ...k,
        functions: functions.length,
        guests: guests.length,
        brideGuests: guests.filter(x => x.Side === 'Bride' || x.Side === 'Female').length,
        groomGuests: guests.filter(x => x.Side === 'Groom' || x.Side === 'Male').length,
        pax: guests.reduce((s, x) => s + Number(x.ExpectedCount || 0), 0),
      }));
    }).catch(() => {});
  }, [event]);

  const cards = [
    { n: kpi.events, l: 'Events', tone: 'blue', to: '/app/events', g: 'linear-gradient(135deg,#2563eb,#0891b2)' },
    { n: kpi.functions, l: 'Functions' + (event ? '' : ' (pick event)'), tone: 'teal', to: '/app/functions', g: 'linear-gradient(135deg,#0f766e,#14b8a6)' },
    { n: kpi.guests, l: 'Total Guests' + (event ? '' : ' (pick event)'), tone: 'pink', to: '/app/members', g: 'linear-gradient(135deg,#be185d,#f43f5e)' },
    isMarriage ? {
      n: guestSide === 'Groom' ? kpi.groomGuests : kpi.brideGuests,
      l: (guestSide === 'Groom' ? 'Groom' : 'Bride') + ' Guests',
      tone: guestSide === 'Groom' ? 'indigo' : 'rose',
      to: '/app/members',
      g: guestSide === 'Groom' ? 'linear-gradient(135deg,#4f46e5,#2563eb)' : 'linear-gradient(135deg,#db2777,#f472b6)'
    } : { n: kpi.guests, l: 'Guests', tone: 'indigo', to: '/app/members', g: 'linear-gradient(135deg,#4f46e5,#2563eb)' },
    { n: kpi.pax, l: 'Expected Pax', tone: 'green', to: '/app/headcount', g: 'linear-gradient(135deg,#15803d,#14b8a6)' },
  ];

  return { organiser, event, kpi, cards, guestSide, mySide, isMarriage };
}
